import { ChangeDetectionStrategy, Component, OnDestroy, inject, model, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService, ENVIRONMENT, ModalService, UserService } from '@cfp/core';
import { isBsatRegistrationClosed } from '@cfp/features-shared/self-onboarding';
import { LoginCard } from '@cfp/shared/ui/login';
import { ContactModal } from '@cfp/shared/ui/modals';
import {
  Alert,
  Button,
  FormGroupActionsDirective,
  FormGroupComponent,
  InputPassword,
  InputText,
  Link,
  ValidationFeedback
} from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { LoginLoadingSpinner, LoginMessages, LoginSso } from '../../components';
import { LoginFormService } from '../../services';

@Component({
  selector: 'cfp-login-page',
  templateUrl: './login-page.html',
  styleUrls: ['./login-page.scss'],
  imports: [
    LoginCard,
    LoginLoadingSpinner,
    TranslateModule,
    LoginMessages,
    RouterLink,
    ReactiveFormsModule,
    FormGroupComponent,
    InputText,
    Link,
    InputPassword,
    Button,
    LoginSso,
    FormGroupActionsDirective,
    ValidationFeedback,
    Alert
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPage implements OnDestroy {
  public readonly isLoading = model(false);

  protected readonly form: FormGroup;
  protected readonly basePath: string = '/login';

  protected readonly changePassword = signal(false);
  protected readonly loginAttempts = signal<{ [username: string]: number }>({});
  protected readonly warningMessages = signal<string[]>([]);
  protected readonly interpolateParams = signal<Record<string, string>>({});
  protected readonly errorMessages = signal<string[]>([]);
  protected readonly loginMessagesDataTest = signal<string | undefined>(undefined);
  protected readonly showLoginAttemptsWarning = signal<boolean>(false);

  protected readonly environment = inject(ENVIRONMENT);
  private readonly loginFormService = inject(LoginFormService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);

  protected readonly loginAllowSelfOnboarding =
    this.environment.loginAllowSelfOnboarding === true &&
    !isBsatRegistrationClosed(this.environment.environmentCode.dto);

  constructor() {
    this.form = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
    const basePath = this.route.snapshot.data?.basePath;
    if (basePath) {
      this.basePath = basePath;
    }
  }

  public ngOnDestroy(): void {
    if (this.changePassword()) {
      this.authService.logout();
    }
  }

  protected closeWindow(): void {
    window.close();
  }

  protected submitForm(): boolean {
    this.changePassword.set(false);
    this.loginMessagesDataTest.set(undefined);

    if (!this.validateLoginAttempts(this.form.value.username) || !this.validateForm()) {
      return false;
    }

    this.isLoading.set(true);

    this.authService.login(this.form.value.username, this.form.value.password).subscribe({
      next: () => {
        this.authService.goToApp(this.route.snapshot);
      },
      error: (error) => {
        this.isLoading.set(false);
        this.userService.changePasswordUserName.set(undefined);

        const result = this.loginFormService.mapLoginAuthError(error);
        this.errorMessages.set(result.errorMessages);
        this.warningMessages.set(result.warningMessages);
        this.interpolateParams.set(result.interpolateParams);
        this.loginMessagesDataTest.set(result.loginMessagesDataTest);
        this.changePassword.set(result.shouldChangePassword);

        if (result.shouldChangePassword) {
          this.userService.changePasswordUserName.set(this.form.value.username);
        }
      }
    });

    return true;
  }

  protected validateForm(): boolean {
    if (this.form.status === 'INVALID') {
      this.errorMessages.set(this.loginFormService.transformErrorMessages(this.form));
      return false;
    }

    this.errorMessages.set([]);
    return true;
  }

  /**
   * If user tries more than 3 times a warning will be emitted
   */
  protected validateLoginAttempts(username: string): boolean {
    this.showLoginAttemptsWarning.set(false);

    const attempts = this.loginAttempts();
    const currentCount = attempts[username] ?? 0;
    this.loginAttempts.set({ ...attempts, [username]: currentCount + 1 });

    if (currentCount + 1 >= 4) {
      this.showLoginAttemptsWarning.set(true);
      return false;
    }

    this.warningMessages.set([]);
    return true;
  }

  protected contactSupport(): void {
    this.modalService.show(ContactModal, {
      initialState: {
        showMessage: true,
        subject: 'Unlock account',
        title: 'login.error.contactSupport.title',
        intro: 'login.error.contactSupport.intro'
      }
    });
  }

  protected routePasswordChange(): void {
    this.changePassword.set(false);
    this.router.navigateByUrl('/login/auth/change');
  }
}
